import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fingerScan } from "careplix-web-algo";
import { sdkURL } from "utils/api-url-list";

const LoadingScreen = ({ screen = "" }) => {
  switch (screen) {
    case "analyzing":
      return (
        <>
          <img
            src={process.env.PUBLIC_URL + "/images/analyzing.webp"}
            alt="analyzing icon"
            className="w-48 mx-auto"
          />
          <p className="mt-3 text-primary text-xl font-medium">Analyzing Data</p>
          <p className="mt-2 text-primary font-light">
            Hold tight, the measurement
            <br />
            results are on the way
          </p>
        </>
      );
    case "success":
      return (
        <>
          <img
            src={process.env.PUBLIC_URL + "/images/success.webp"}
            alt="success icon"
            className="w-48 mx-auto"
          />
          <p className="mt-3 text-primary">Scan Successful...</p>
        </>
      );
    case "failure":
      return (
        <>
          <img
            src={process.env.PUBLIC_URL + "/images/failure.webp"}
            alt="failure icon"
            className="w-48 mx-auto"
          />
          <p className="mt-3 text-primary">
            Oops..
            <br />
            We couldn't get enough data to process...
            <br />
            Please try again.
          </p>
        </>
      );
    default:
      return <></>;
  }
};

const scanMessages = [
  "We burn calories while we are asleep because brain activity requires energy",
  "Exercise promotes cardiovascular health, ensuring more blood and oxygen to circulate the body, helping to elevate energy",
  "Always sleep on your back straight since it allows your neck and spine in a neutral position",
  "Alcohol consumption disrupts chemical impulses between brain cells causing impulsive behaviour, slurred speech, impaired memory",
  "Meditation increases nitric oxide generation in the brain helping to lower your blood pressure",
  "Meditation reduces the production of cytokines in several recent studies, thus helping in stress reduction",
  "Laughing is good for your heart. It reduces stress and gives a boost to your immune system",
  "Lack of sleep can lead to heart disease, heart attack, or stroke",
  "Exercise boosts brain performance",
  "Heart pumps about 2,000 gallons of blood every day",
  "Your heart will beat about 115,000 times each day",
].sort(() => Math.random() - 0.5);

const getMessage = (type = "", timeElapsed = 0) => {
  if (type !== "scan") {
    if (timeElapsed <= 5000) return "Cover your back camera with your finger";
    else if (timeElapsed <= 10000) return "During the measurement, please do not speak or move";
    else if (timeElapsed <= 17000)
      return "Keep your device steady throughout the vital measurement";
    else if (timeElapsed <= 18000) return "We are good to start in 3";
    else if (timeElapsed <= 19000) return "We are good to start in 2";
    else if (timeElapsed <= 20000) return "We are good to start in 1";
  } else {
    if (timeElapsed <= 30000) return "Scan in Progress...";
    else if (timeElapsed <= 40000) return scanMessages[0];
    else if (timeElapsed <= 50000) return scanMessages[1];
    else if (timeElapsed <= 60000) return scanMessages[2];
    else if (timeElapsed <= 70000) return scanMessages[3];
    else if (timeElapsed <= 80000) return scanMessages[4];
    else if (timeElapsed <= 90000) return scanMessages[5];
    else if (timeElapsed <= 100000) return scanMessages[6];
    else if (timeElapsed <= 110000) return scanMessages[7];
    else if (timeElapsed <= 120000) return scanMessages[8];
    else if (timeElapsed <= 130000) return scanMessages[9];
    else if (timeElapsed <= 140000) return scanMessages[10];
  }
};

function FingerScan(props) {
  const location = useLocation();
  const navigate = useNavigate();

  const [loadingScreen, setLoadingScreen] = useState("");
  const [scanFrameData, setScanFrameData] = useState({
    type: "",
    timeElapsed: 0,
    confidence: 0,
    fps: 0,
  });

  useEffect(() => {
    const { scan_token, scan_duration, patientData } = location.state ?? {};
    if (scan_token?.length > 0) {
      fingerScan.onFrame(setScanFrameData);
      fingerScan.onScanFinish(async ({ raw_intensity, ppg_time, average_fps }) => {
        try {
          setLoadingScreen("analyzing");
          let dob = new Date();
          dob.setFullYear(dob.getFullYear() - patientData.age);
          dob = dob.toISOString().split("T")[0];
          const resp = await fetch(sdkURL("/trial/add-scan"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              api_key: localStorage.getItem("client_code"),
              scan_token: scan_token,
              employee_id: localStorage.getItem("emp_id"),
              posture: "Resting",
              dob,
              gender: patientData.gender,
              notes: {
                basic_info: {
                  patient_id: patientData.patientID,
                  name: patientData.name ?? "",
                  dob,
                  age: patientData.age,
                  gender: patientData.gender,
                  height: patientData.height ?? "",
                  weight: patientData.weight ?? "",
                  waist: patientData.waist ?? "",
                  email: null,
                  mobile_no: null,
                },
                observations: [],
              },
              metadata: {
                physiological_scores: { height: patientData.height, weight: patientData.weight },
                ppg_time: ppg_time,
                raw_intensity: raw_intensity,
                device: navigator.platform.match(/iPhone|iPod|iPad/)
                  ? "RPPG_CAREPLIX_FINGER_IOS"
                  : "RPPG_CAREPLIX_FINGER_ANDROID",
                fps: average_fps,
              },
              info: `${navigator.userAgent} | ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
            }),
          });
          const resp_json = await resp.json();
          if (resp_json.statusCode?.toString().startsWith("2")) {
            setLoadingScreen("success");
            setTimeout(
              () => navigate("/result", { state: { result: resp_json }, replace: true }),
              3000
            );
          } else throw new Error(resp_json.message);
        } catch (err) {
          setLoadingScreen("failure");
          toast(err.message, {
            type: "error",
            position: "top-center",
            hideProgressBar: false,
            draggable: false,
            pauseOnHover: false,
            pauseOnFocusLoss: false,
            closeOnClick: false,
            autoClose: 3000,
            onClose: cancelBtnFunc,
          });
        }
      });
      fingerScan.onError(err => {
        setLoadingScreen("failure");
        toast(err.message ?? "Measurement failed due to inappropriate data. Please try again.", {
          type: "error",
          position: "top-center",
          hideProgressBar: false,
          draggable: false,
          pauseOnHover: false,
          pauseOnFocusLoss: false,
          closeOnClick: false,
          autoClose: 3000,
          onClose: cancelBtnFunc,
        });
      });
      fingerScan
        .startScan(scan_duration, scan_duration)
        .then(() => console.log("Scan Started"))
        .catch(console.error);

      // Enable wake lock.
      let wakeLock = null;
      navigator.wakeLock
        ?.request("screen")
        .then(wakeLockSentinel => {
          wakeLock = wakeLockSentinel;
          console.log("Wake Lock is active!");
        })
        .catch(err => {
          console.log("Wakelock Error");
          console.error(err);
        });
      // The Wake Lock request has failed - usually system related, such as battery.

      return () => {
        fingerScan.stopScan(true);
        wakeLock
          ?.release()
          .then(() => console.log("Wake Lock Released!"))
          .catch(err => {
            console.log("Wakelock Error");
            console.error(err);
          });
      };
    } else cancelBtnFunc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelBtnFunc = () => {
    fingerScan.stopScan(true);
    navigate("/select-option", {
      replace: true,
      state: { patientData: location.state?.patientData },
    });
  };

  return (
    <div className="relative h-screen bg-white">
      {loadingScreen.length > 0 ? (
        <div className="absolute inset-0 bg-white px-8 py-12 flex flex-col items-center justify-center text-center">
          <LoadingScreen screen={loadingScreen} />
        </div>
      ) : (
        <>
          <div className="relative h-full w-full">
            <video id="videoInput" width={0} height={0} autoPlay muted hidden playsInline />
            <canvas id="canvasOutput" className="h-full w-full -scale-x-100" />
            <div className="fixed inset-x-0 top-[20vh]">
              {fingerScan.isFingerInView() ? (
                <img
                  src={process.env.PUBLIC_URL + "/images/heartbeat.webp"}
                  alt="heartbeat"
                  className="w-48 mx-auto"
                />
              ) : (
                <>
                  <svg className="w-48 mx-auto" viewBox="0 0 300 90">
                    <circle fill="#fff" cx="278.63" cy="55.48" r="13.33" />
                    <polyline
                      fill="none"
                      stroke="#fff"
                      strokeWidth={5}
                      strokeMiterlimit={10}
                      points="7.75,54.04 74.69,54.04 83.58,21.09 96.13,72.86 102.93,54.04 118.61,53.51 130.12,10.11 
	158.36,80.71 169.86,54.04 189.73,54.04 198.1,23.18 200.71,23.18 212.22,68.68 217.45,54.04 265.57,53.96 "
                    />
                  </svg>
                  <p className="mt-2 text-xl text-center text-white drop-shadow">
                    Cannot Detect Finger
                  </p>
                </>
              )}
            </div>
            {fingerScan.canStop() && (
              <button
                type="button"
                onClick={() => fingerScan.stopScan()}
                className="fixed top-2.5 left-2.5 rounded-full px-3 py-1.5 text-sm text-white bg-[#ff0002]"
              >
                Finish Scan
              </button>
            )}
            <div
              className="fixed top-2.5 right-2.5 rounded-full px-3 py-1.5 text-sm text-white bg-[#ff0002]"
              style={{
                backgroundColor:
                  scanFrameData.confidence > 0.5
                    ? scanFrameData.confidence > 0.9
                      ? "#a0dd0c"
                      : "#eea508"
                    : "#f31e2e",
              }}
            >
              Signal Strength:&nbsp;
              <span className="font-semibold">
                {scanFrameData.confidence > 0.5
                  ? scanFrameData.confidence > 0.9
                    ? "Good"
                    : "Moderate"
                  : "Poor"}
              </span>
            </div>
            <div className="fixed bottom-0 inset-x-0 px-4 pt-6 pb-8 bg-white/80 border-t border-lightgray text-center">
              <h3 className="text-xl text-secondary font-semibold">
                {scanFrameData.type === "scan"
                  ? `${Math.round(
                      ((scanFrameData.timeElapsed - 20000) /
                        (location.state?.scan_duration ?? 60000)) *
                        100
                    )}% Completed`
                  : `Calibration in progress...`}
              </h3>
              <h4 className="mt-2 text-primary">
                {getMessage(scanFrameData.type, scanFrameData.timeElapsed)}
              </h4>
              <button
                type="button"
                onClick={cancelBtnFunc}
                className="block mt-4 mx-auto px-4 py-2 w-48 rounded-full text-primary border border-primary text-xl font-medium"
              >
                Cancel Scan
              </button>
            </div>
          </div>
          {fingerScan.isInitializing() && (
            <div className="absolute inset-0 bg-white px-8 py-12 flex flex-col items-center justify-center text-center">
              <img
                src={process.env.PUBLIC_URL + "/images/camera.webp"}
                alt="camera"
                className="w-48 mx-auto"
              />
              <h3 className="mt-3 text-primary text-xl font-medium">Accessing Camera</h3>
              <h4 className="mt-4 text-primary font-light">Camera not opening?</h4>
              <p className="mt-1 text-primary font-light">
                In case your camera is not opening, you need to allow camera permission from your
                browser settings and phone settings.
              </p>
              <button
                type="button"
                onClick={cancelBtnFunc}
                className="block mt-6 mx-auto px-4 py-2 w-48 rounded-full bg-white text-primary border border-primary text-xl font-medium"
              >
                Cancel Scan
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default FingerScan;
